import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  type ChangeEvent,
} from "react";

import styles from "~/pages/CharacterSheet/CharacterSheet.scss";
import CoreStatControl from "~/pages/CharacterSheet/widgets/CoreStatControl/CoreStatControl";
import DerivedStatItem from "~/pages/CharacterSheet/widgets/DerivedStatItem/DerivedStatItem";
import SkillCardBase from "~/pages/CharacterSheet/widgets/SkillCardBase/SkillCardBase";
import RollResultDisplay from "~/pages/CharacterSheet/widgets/RollResultDisplay/RollResultDisplay";
import CharacterNameInput from "~/pages/CharacterSheet/widgets/CharacterNameInput/CharacterNameInput";
import PointTracker from "~/pages/CharacterSheet/widgets/PointTracker/PointTracker";
import MovementCard from "~/pages/CharacterSheet/widgets/MovementCard/MovementCard";
import StrainTracker from "~/pages/CharacterSheet/widgets/StrainTracker/StrainTracker";
import RollSearch from "~/pages/CharacterSheet/widgets/RollSearch/RollSearch";
import ItemsSection from "~/pages/CharacterSheet/widgets/ItemsSection/ItemsSection";
import ProfileSection from "~/pages/CharacterSheet/widgets/ProfileSection/ProfileSection";
import RelationshipsSection from "~/pages/CharacterSheet/widgets/RelationshipsSection/RelationshipsSection";
import ActionButtons from "~/pages/CharacterSheet/widgets/ActionButtons/ActionButtons";
import CharacterSearch from "~/widgets/CharacterSearch/CharacterSearch";
import TableSearch from "~/widgets/TableSearch/TableSearch";
import EmptyTableState from "~/widgets/EmptyTableState/EmptyTableState";
import SettingsPopup from "~/widgets/SettingsPopup/SettingsPopup";
import { Badge } from "~/widgets/Badge/Badge";
import { generateUUID } from "~/utils/uuid";
import { useFocus } from "~/contexts/FocusContext";
import { AppCommand } from "~/types/commands";
import { getCharacterActions } from "~/pages/CharacterSheet/characterActions";
import { TagAction, TagActionType } from "~/types/character";
import { CharacterContextProvider } from "~/contexts/CharacterContext";
import { getCoreStats } from "~/utils/glossaryStats";
import { useCharacterStore } from "~/stores/characterStore";
import { useNotifications } from "~/contexts/useNotifications";
import { useDialog } from "~/contexts/useDialog";
import { useRolling } from "~/hooks/useRolling";
import { useDisplayedCharacter, useItemsWithEditState, useRestActions } from "~/pages/CharacterSheet/hooks";
import { calculateStrainDamage } from "~/rules/strain";
import {
  DAMAGE_DIALOG,
  HEAL_DIALOG,
  DIRECT_DAMAGE_DIALOG,
  SPEND_RESILIENCE_DIALOG,
  DELETE_CHARACTER_DIALOG,
  getTagActionDialog,
} from "~/rules/dialogMessages";
import { CHARACTER_DATA_VERSION, CharacterData } from "~/types/storage";
import { computeMovement, computeHpMax, computeResilienceMax, computeSkills } from "~/utils/characterDerived";


// Get core stats from glossary (static)
const CORE_STAT_ENTRIES = getCoreStats();

/**
 * CharacterSheet Component
 *
 * Main character sheet interface for the Divide TTRPG system.
 *
 * @hotkeys
 * - `/`: Open Command Palette
 * - `S`: Focus on Strain Widget
 *   - `+` or `=`: Add 1 Strain (when Strain Widget is focused)
 *   - `-`: Remove 1 Strain (when Strain Widget is focused)
 * - `D`: Open Damage Dialog
 * - `H`: Open Healing Dialog
 * - `X`: Open Direct Damage Dialog
 * - `V`: Open Spend Dialog Resilience
 * - `R`: Open Roll Search
 * - `C`: Open Character Switcher
 * - `T`: Open Table Switcher
 *
 * Note: Hotkeys are disabled when typing in input fields.
 */
function CharacterSheet(): React.ReactElement {
  // Access character data and update functions via hook
  const {
    characterData,
    displayedCharacterId,
    viewingCharacterId: _viewingCharacterId,
    updateName,
    updateCoreStats,
    updateCurrentHp,
    updateCurrentResilience,
    updateStrain,
    updateItems,
    updateAppearance,
    updateRelationships,
    updateNotes,
    exportCharacter,
    clearViewingOverride,
  } = useDisplayedCharacter();

  // Access global character management
  const allCharacters = useCharacterStore((state) => state.characters);
  const getActiveCharacter = useCharacterStore((state) => state.getActiveCharacter);
  const getCharactersForActiveTable = useCharacterStore((state) => state.getCharactersForActiveTable);
  const setActiveCharacter = useCharacterStore((state) => state.setActiveCharacter);
  const addCharacter = useCharacterStore((state) => state.addCharacter);
  const removeCharacter = useCharacterStore((state) => state.removeCharacter);
  const importCharacter = useCharacterStore((state) => state.importCharacter);

  // Access table management
  const tables = useCharacterStore((state) => state.tables);
  const activeTableId = useCharacterStore((state) => state.activeTableId);
  const getActiveTable = useCharacterStore((state) => state.getActiveTable);
  const setActiveTable = useCharacterStore((state) => state.setActiveTable);
  const addTable = useCharacterStore((state) => state.addTable);
  const removeTable = useCharacterStore((state) => state.removeTable);
  const renameTable = useCharacterStore((state) => state.renameTable);
  const assignCharacterToTable = useCharacterStore((state) => state.assignCharacterToTable);

  const activeCharacterId = getActiveCharacter()?.id || '';
  const tableCharacters = getCharactersForActiveTable();
  const activeTable = getActiveTable();

  // Extract data from displayed character
  const { name, coreStats, currentHp, currentResilience, strain, items, appearance, relationships, notes } =
    characterData;

  // Extract individual core stats (for backwards compatibility with existing code)
  const mind = coreStats["Mind"] ?? 0;
  const body = coreStats["Body"] ?? 0;
  const reflex = coreStats["Reflex"] ?? 0;

  // Helper to update a single core stat
  const updateCoreStat = useCallback(
    (statId: string, value: number) => {
      const newStats = { ...coreStats, [statId]: value };
      updateCoreStats(newStats);
    },
    [coreStats, updateCoreStats]
  );

  const movement = computeMovement(coreStats);
  const hpMax = computeHpMax(coreStats);
  const resilienceMax = computeResilienceMax(coreStats);

  // UI state (modals, search)
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
  const [isCharacterSearchOpen, setIsCharacterSearchOpen] = useState<boolean>(false);
  const [isTableSearchOpen, setIsTableSearchOpen] = useState<boolean>(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);

  // Use global notifications and dialog contexts
  const { rollHistory, clearAllRolls, clearRoll, addTagNotification } = useNotifications();
  const { openDialog } = useDialog();

  // Use shared rolling hook
  const { rollDice } = useRolling();

  // Use items hook with edit state
  const {
    itemsWithEditState,
    addItem,
    toggleEditItem,
    updateItemContent,
    moveItem,
    handleReorder,
    deleteItem,
  } = useItemsWithEditState(items, updateItems, (itemId, itemName) => {
    // Open delete confirmation dialog
    openDialog({
      type: 'confirm',
      title: 'Delete Item',
      message: `Delete ${itemName}?`,
      onConfirm: () => {
        const updatedItems = items.filter((item) => item.id !== itemId);
        updateItems(updatedItems);
      },
    });
  });

  // Use rest actions hook
  const { takeSnackBreak, takeMealBreak, takeFullRest } = useRestActions(
    coreStats,
    currentResilience,
    currentHp,
    strain,
    updateCurrentResilience,
    updateCurrentHp,
    updateStrain
  );

  const handleImport = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await importCharacter(file);
      // Context automatically updates, no need to reload local state
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      alert("Error importing character data.");
    }

    e.target.value = "";
  };

  // Ref for the strain tracker container to support focus
  const strainTrackerRef = useRef<HTMLDivElement>(null);
  const [isStrainFocused, setIsStrainFocused] = useState<boolean>(false);

  const totalPoints = mind + body + reflex;

  const handleStatChange = (
    statId: string,
    event: ChangeEvent<HTMLInputElement>,
  ): void => {
    const numValue = Math.max(0, parseInt(event.target.value, 10) || 0);
    updateCoreStat(statId, numValue);

    // Special handling for Body changes (affects HP and Resilience max)
    if (statId === "Body") {
      const newCoreStats = { ...coreStats, [statId]: numValue };
      const newHpMax = computeHpMax(newCoreStats);
      const newResilienceMax = computeResilienceMax(newCoreStats);
      updateCurrentHp(Math.min(currentHp, newHpMax));
      updateCurrentResilience(Math.min(currentResilience, newResilienceMax));
    }
    // Special handling for Mind changes (affects Resilience max)
    if (statId === "Mind") {
      const newCoreStats = { ...coreStats, [statId]: numValue };
      const newResilienceMax = computeResilienceMax(newCoreStats);
      updateCurrentResilience(Math.min(currentResilience, newResilienceMax));
    }
  };

  // Dialog helpers using new generic dialog system
  const openDamageDialog = useCallback((): void => {
    openDialog({
      type: 'input',
      ...DAMAGE_DIALOG,
      subtitle: name,
      onConfirm: (amount) => {
        let remainingDamage = amount;
        if (currentResilience > 0) {
          const resilienceDamage = Math.min(currentResilience, remainingDamage);
          updateCurrentResilience(currentResilience - resilienceDamage);
          remainingDamage -= resilienceDamage;
        }
        if (remainingDamage > 0) {
          updateCurrentHp(Math.max(0, currentHp - remainingDamage));
        }
      },
    });
  }, [openDialog, name, currentResilience, currentHp, updateCurrentResilience, updateCurrentHp]);

  const openHealDialog = useCallback((): void => {
    openDialog({
      type: 'input',
      ...HEAL_DIALOG,
      subtitle: name,
      onConfirm: (amount) => {
        updateCurrentHp(Math.min(hpMax, currentHp + amount));
      },
    });
  }, [openDialog, name, hpMax, currentHp, updateCurrentHp]);

  const openDirectDamageDialog = useCallback((): void => {
    openDialog({
      type: 'input',
      ...DIRECT_DAMAGE_DIALOG,
      subtitle: name,
      onConfirm: (amount) => {
        updateCurrentHp(Math.max(0, currentHp - amount));
      },
    });
  }, [openDialog, name, currentHp, updateCurrentHp]);

  const openSpendResilienceDialog = useCallback((): void => {
    openDialog({
      type: 'input',
      ...SPEND_RESILIENCE_DIALOG,
      subtitle: name,
      onConfirm: (amount) => {
        updateCurrentResilience(Math.max(0, currentResilience - amount));
      },
    });
  }, [openDialog, name, currentResilience, updateCurrentResilience]);

  const _openAcquireStrainDialog = useCallback((): void => {
    openDialog({
      type: 'input',
      title: 'Acquire Strain',
      inputLabel: 'Amount',
      onConfirm: (amount) => {
        updateStrain(strain + amount);
      },
    });
  }, [openDialog, strain, updateStrain]);

  const _openReduceStrainDialog = useCallback((): void => {
    openDialog({
      type: 'input',
      title: 'Reduce Strain',
      inputLabel: 'Amount',
      onConfirm: (amount) => {
        updateStrain(Math.max(0, strain - amount));
      },
    });
  }, [openDialog, strain, updateStrain]);

  const handleStrainDamage = useCallback((damage: number): void => {
    // Apply strain damage directly to HP, bypassing Aura/Resilience
    updateCurrentHp(Math.max(0, currentHp - damage));

    // Show notification for strain damage applied
    addTagNotification({
      type: TagActionType.LOSE_HEALTH,
      value: damage,
      sourceName: "End of Combat Strain",
    });
  }, [currentHp, updateCurrentHp, addTagNotification]);

  const handleTagAction = useCallback((action: TagAction): void => {
    const { type, value = 0, sourceName, sourceType } = action;

    // Get dialog configuration from centralized messages
    const { sourceLabel, ...dialogConfig } = getTagActionDialog(type, value, sourceName, sourceType);

    // Open confirmation dialog
    openDialog({
      type: 'confirm',
      ...dialogConfig,
      subtitle: sourceLabel,
      onConfirm: () => {
        // Add notification
        addTagNotification(action);

        // Apply effect
        switch (type) {
          case TagActionType.GAIN_HEALTH:
            updateCurrentHp(Math.min(hpMax, currentHp + value));
            break;
          case TagActionType.LOSE_HEALTH:
            updateCurrentHp(Math.max(0, currentHp - value));
            break;
          case TagActionType.LOSE_RESILIENCE:
            updateCurrentResilience(Math.max(0, currentResilience - value));
            break;
          case TagActionType.ACQUIRE_STRAIN:
            updateStrain(strain + value);
            break;
          case TagActionType.REDUCE_STRAIN:
            updateStrain(Math.max(0, strain - value));
            break;
        }
      },
    });
  }, [openDialog, addTagNotification, hpMax, currentHp, currentResilience, strain, updateCurrentHp, updateCurrentResilience, updateStrain]);

  // Command palette action handlers
  const applyStrainDamage = useCallback((): void => {
    const damage = calculateStrainDamage(strain);
    if (damage > 0) {
      handleStrainDamage(damage);
    }
  }, [strain, handleStrainDamage]);



  const createNewCharacter = useCallback((): void => {
    // Create a new blank character with default stats
    const defaultCoreStats: Record<string, number> = {};
    CORE_STAT_ENTRIES.forEach((entry) => {
      defaultCoreStats[entry.id] = 2;
    });

    const defaultHpMax = computeHpMax(defaultCoreStats);
    const defaultResMax = computeResilienceMax(defaultCoreStats);

    const newCharacter: CharacterData = {
      id: generateUUID(),
      version: CHARACTER_DATA_VERSION,
      name: "New Character",
      coreStats: defaultCoreStats,
      currentHp: defaultHpMax,
      currentResilience: defaultResMax,
      strain: 0,
      items: [],
      appearance: "",
      relationships: [],
      notes: "",
    };

    addCharacter(newCharacter);
    setActiveCharacter(newCharacter.id);
  }, [addCharacter, setActiveCharacter]);

  const deleteCurrentCharacter = useCallback((): void => {
    if (allCharacters.length <= 1) {
      // Can't delete the last character
      return;
    }
    openDialog({
      type: 'confirm',
      ...DELETE_CHARACTER_DIALOG,
      subtitle: characterData.name,
      message: DELETE_CHARACTER_DIALOG.getMessage(characterData.name),
      onConfirm: () => {
        removeCharacter(displayedCharacterId);
      },
    });
  }, [allCharacters.length, openDialog, characterData.name, displayedCharacterId, removeCharacter]);

  const moveCharacterToTable = useCallback((): void => {
    openDialog({
      type: 'select',
      title: 'Move Character to Table',
      subtitle: characterData.name,
      message: 'Select a table to move this character to:',
      options: tables.map((table) => ({
        id: table.id,
        label: table.name,
        disabled: table.id === activeTableId,
      })),
      confirmLabel: 'Move',
      onConfirm: (tableId) => {
        assignCharacterToTable(displayedCharacterId, tableId);
        // Switch to the destination table
        setActiveTable(tableId);
      },
    });
  }, [openDialog, characterData.name, tables, activeTableId, assignCharacterToTable, displayedCharacterId, setActiveTable]);

  const deleteCurrentTable = useCallback((): void => {
    const table = getActiveTable();
    if (!table) return;

    if (table.id === 'Uncategorized' || table.id === '__uncategorized__') {
      // Can't delete the uncategorized table
      return;
    }

    openDialog({
      type: 'confirm',
      title: 'Delete Table',
      subtitle: table.name,
      message: `Delete this table? Characters in this table will be moved to Uncategorized.`,
      confirmLabel: 'Delete',
      theme: 'negative',
      onConfirm: () => {
        removeTable(table.id);
      },
    });
  }, [getActiveTable, openDialog, removeTable]);

  const renameCurrentTable = useCallback((): void => {
    const table = getActiveTable();
    if (!table) return;

    openDialog({
      type: 'text-input',
      title: 'Rename Table',
      subtitle: table.name,
      inputLabel: 'New Name',
      initialValue: table.name,
      confirmLabel: 'Rename',
      onConfirm: (newName) => {
        if (newName.trim()) {
          renameTable(table.id, newName.trim());
        }
      },
    });
  }, [getActiveTable, openDialog, renameTable]);

  const importCharacterFromTable = useCallback((): void => {
    // Get all characters from other tables
    const otherCharacters = allCharacters.filter((c: CharacterData) => c.belongsToTable !== activeTableId);

    if (otherCharacters.length === 0) {
      openDialog({
        type: 'confirm',
        title: 'No Characters Available',
        message: 'There are no characters in other tables to import.',
        confirmLabel: 'OK',
        onConfirm: () => {},
      });
      return;
    }

    // Build options with table context
    const options = otherCharacters.map((char: CharacterData) => {
      const charTable = tables.find((t) => t.id === char.belongsToTable);
      return {
        id: char.id,
        label: `${char.name} (from ${charTable?.name || 'Unknown'})`,
      };
    });

    openDialog({
      type: 'select',
      title: 'Import Character',
      subtitle: getActiveTable()?.name,
      message: 'Select a character to copy to this table:',
      options,
      confirmLabel: 'Import',
      onConfirm: (characterId) => {
        const sourceChar = allCharacters.find((c: CharacterData) => c.id === characterId);
        if (sourceChar) {
          // Create a copy of the character with a new ID and assign to current table
          const newCharacter = {
            ...sourceChar,
            id: generateUUID(),
            belongsToTable: activeTableId,
          };
          addCharacter(newCharacter);
          setActiveCharacter(newCharacter.id);
        }
      },
    });
  }, [allCharacters, activeTableId, tables, openDialog, getActiveTable, addCharacter, setActiveCharacter]);

  const handleCommand = useCallback(
    (command: AppCommand) => {
      switch (command.type) {
        case "damage":
          openDamageDialog();
          break;
        case "heal":
          openHealDialog();
          break;
        case "direct-damage":
          openDirectDamageDialog();
          break;
        case "spend-resilience":
          openSpendResilienceDialog();
          break;
        case "end-combat":
          applyStrainDamage();
          break;
        case "focus-strain":
          strainTrackerRef.current?.focus();
          setIsStrainFocused(true);
          break;
        case "strain-increase":
          updateStrain(strain + 1);
          break;
        case "strain-decrease":
          updateStrain(Math.max(0, strain - 1));
          break;

        case "snack-break":
          takeSnackBreak();
          break;
        case "meal-break":
          takeMealBreak();
          break;
        case "full-rest":
          takeFullRest();
          break;
        case "open-roll-search":
          setIsSearchOpen(true);
          break;
        case "open-character-search":
          setIsCharacterSearchOpen(true);
          break;
        case "open-table-search":
          setIsTableSearchOpen(true);
          break;
        case "new-character":
          createNewCharacter();
          break;
        case "delete-character":
          deleteCurrentCharacter();
          break;
        case "move-character-to-table":
          moveCharacterToTable();
          break;
        case "delete-table":
          deleteCurrentTable();
          break;
        case "rename-table":
          renameCurrentTable();
          break;
        case "import-character-from-table":
          importCharacterFromTable();
          break;
      }
    },
    [
      openDamageDialog,
      openHealDialog,
      openDirectDamageDialog,
      openSpendResilienceDialog,
      applyStrainDamage,
      takeSnackBreak,
      takeMealBreak,
      takeFullRest,
      createNewCharacter,
      deleteCurrentCharacter,
      moveCharacterToTable,
      deleteCurrentTable,
      renameCurrentTable,
      importCharacterFromTable,
      isStrainFocused,
      updateStrain,
      strain,
    ]
  );

  const focusActions = useMemo(
    () => getCharacterActions(characterData),
    [characterData]
  );

  const { Scope } = useFocus(
    "CharacterSheet",
    { characterId: activeCharacterId },
    handleCommand,
    focusActions
  );

  const characterActions = useMemo(() => ({
    onRoll: rollDice,
    onTagAction: handleTagAction,
  }), [rollDice, handleTagAction]);

  // Compute skills for local display (also computed in context)
  const skills = useMemo(() => computeSkills(coreStats), [coreStats]);

  // Get delete target name for dialog
  // Check if current table has any characters
  const hasCharacters = tableCharacters.length > 0;

  return (
    <CharacterContextProvider characterData={characterData} actions={characterActions}>
      <div className={styles.container}>
        <Scope>
          {!hasCharacters ? (
            <EmptyTableState
              tableName={activeTable?.name || 'this table'}
              onCreateCharacter={createNewCharacter}
            />
          ) : (
            <>
              <header className={styles.header}>
                <CharacterNameInput
                  value={name}
                  onChange={updateName}
                  labelSuffix={
                    <div
                      onClick={() => setIsTableSearchOpen(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setIsTableSearchOpen(true);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      style={{
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}
                      title={`Current table: ${activeTable?.name || 'Unknown'} (click to switch)`}
                    >
                      <Badge variant="navigation">{activeTable?.name || 'Unknown'}</Badge>
                    </div>
                  }
                />
                <ActionButtons
                  onDamage={openDamageDialog}
                  onHeal={openHealDialog}
                  onDirectDamage={openDirectDamageDialog}
                  onSpendResilience={openSpendResilienceDialog}
                  onImport={handleImport}
                  onExport={exportCharacter}
                  onOpenCharacterSearch={() => setIsCharacterSearchOpen(true)}
                  onOpenSettings={() => setIsSettingsOpen(true)}
                />
              </header>

              {/* Profile Section */}
              <div className={styles.profileRow}>
                <ProfileSection
                  appearance={appearance}
                  notes={notes}
                  onAppearanceChange={updateAppearance}
                  onNotesChange={updateNotes}
                />
                <RelationshipsSection
                  relationships={relationships}
                  onRelationshipsChange={updateRelationships}
                />
              </div>

              <div className={styles.sheetLayout}>
                {/* Left Column: Stats & Skills */}
                <div className={styles.leftColumn}>
                  <section className={styles.statGrid}>
                    {/* Core Stats */}
                    <div>
                      <div className={styles.sectionHeader}>
                        <h3 className={styles.sectionTitle}>Core Stats</h3>
                        <PointTracker current={totalPoints} target={7} />
                      </div>
                      <div className={styles.statControlGroup}>
                        {CORE_STAT_ENTRIES.map(entry => (
                          <CoreStatControl
                            key={entry.id}
                            label={entry.id}
                            value={coreStats[entry.id] ?? 0}
                            glossaryId={entry.id}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              handleStatChange(entry.id, e);
                            }}
                            id={`${entry.id.toLowerCase()}Stat`}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Derived Stats */}
                    <div className={styles.derivedStats}>
                      <h3 className={styles.sectionTitle}>Derived Stats</h3>

                      <DerivedStatItem
                        label="HP"
                        value={currentHp}
                        max={hpMax}
                        onChange={updateCurrentHp}
                        glossaryId="Health Points"
                      />

                      <DerivedStatItem
                        label="Resilience"
                        value={currentResilience}
                        max={resilienceMax}
                        onChange={updateCurrentResilience}
                        glossaryId="Resilience"
                      />
                    </div>
                  </section>

                  {/* Movement & Strain - spans full width below stats */}
                  <div className={styles.compactGrid}>
                    <MovementCard value={movement} onRoll={rollDice} />

                    <div style={{ position: 'relative' }}>
                      <div className={styles.strainTrackerWrapper}>
                        <StrainTracker
                          ref={strainTrackerRef}
                          value={strain}
                          onChange={updateStrain}
                          onApplyDamage={handleStrainDamage}
                          onCommand={handleCommand}
                          onFocus={() => { setIsStrainFocused(true); }}
                          onBlur={() => { setIsStrainFocused(false); }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Skill Checks Section */}
                  <section className={styles.skillsSection}>
                    <div className={styles.sectionHeader}>
                      <h3 className={styles.sectionTitle}>Skill Checks</h3>
                      <span className={styles.metaLabel} title="Click any skill to roll dice, or press 'R' to open the roll search menu">
                        Click to roll • Press R
                      </span>
                    </div>
                    <div className={styles.skillsGrid}>
                      {skills.map((skill) => (
                        <SkillCardBase
                          key={skill.name}
                          name={skill.name}
                          formula={skill.formula}
                          poolSize={skill.pool}
                          glossaryId={skill.glossaryId}
                          onRoll={rollDice}
                        />
                      ))}
                    </div>
                  </section>

                  {/* Roll Results Display */}
                  <RollResultDisplay
                    rolls={rollHistory}
                    onClearAll={clearAllRolls}
                    onClearRoll={clearRoll}
                  />
                </div>

                {/* Right Column: Items & Abilities */}
                <div className={styles.rightColumn}>
                  <ItemsSection
                    items={itemsWithEditState}
                    onAddItem={addItem}
                    onToggleEdit={toggleEditItem}
                    onUpdate={updateItemContent}
                    onDelete={deleteItem}
                    onMove={moveItem}
                    onReorder={handleReorder}
                  />
                </div>
              </div>
            </>
          )}

          <RollSearch
            isOpen={isSearchOpen}
            onClose={() => setIsSearchOpen(false)}
            skills={skills}
            coreStats={coreStats}
            movement={movement}
            onRoll={rollDice}
          />
          <CharacterSearch
            isOpen={isCharacterSearchOpen}
            onClose={() => setIsCharacterSearchOpen(false)}
            characters={tableCharacters}
            activeCharacterId={displayedCharacterId}
            onSelect={(id) => {
              // Clear URL param and local override, switch to selected character
              clearViewingOverride();
              setActiveCharacter(id);
            }}
          />
          <TableSearch
            isOpen={isTableSearchOpen}
            onClose={() => setIsTableSearchOpen(false)}
            tables={tables}
            activeTableId={activeTableId}
            onSelect={(id) => {
              setActiveTable(id);
              setIsTableSearchOpen(false);
            }}
            onCreateNew={(name) => {
              addTable(name);
            }}
          />
          <SettingsPopup
            isOpen={isSettingsOpen}
            onClose={() => setIsSettingsOpen(false)}
          />
        </Scope>
      </div>
    </CharacterContextProvider>
  );
}

export default CharacterSheet;
